import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import MyTypography from "@saleor/components/MyTypography";
import { defaultListSettings } from "@saleor/config";
import {
  PermissionEnum,
  SapListOfSaleOrderPendingInput,
  useDownloadPendingOrderReportMutation,
  useListOfSaleOrderPendingSapLazyQuery,
  usePrintPendingOrderReportMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import { useLocalPaginationState } from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import BottomBar from "@saleor/PendingOrder/components/BottomBar";
import OrderFilterSectionCustomer from "@saleor/PendingOrder/components/OrderFilterSectionCustomer";
import OrderListTableAdmin from "@saleor/PendingOrder/components/OrderListTableAdmin";
import {
  PendingOrderListSortField,
  PendingOrderListUrl,
  PendingOrderUrlQueryParams
} from "@saleor/PendingOrder/urls";
import { ListViews } from "@saleor/types";
import {
  asSortParams,
  getSortParams,
  getSortUrlVariables
} from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface OrdersViewProps {
  params: PendingOrderUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  () => ({
    container: {
      marginBottom: "7rem"
    },
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      "& .red": {
        color: "red",
        display: "inline-block"
      }
    }
  }),
  { name: "OrdersView" }
);

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex}>
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
          color={props.color}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const PendingOrderViewCustomer: React.FC<OrdersViewProps> = () => {
  const intl = useIntl();
  const [_, dispatchAppState] = useAppState();

  const qs = parseQs(location.search.substr(1));
  const params = asSortParams(
    qs,
    PendingOrderListSortField,
    PendingOrderListSortField.confirmDate
  );
  const navigate = useNavigator();
  const { user } = useUser();
  const [firstRender, setFirstRender] = useState<boolean>(true);

  const isCustomer = useMemo(
    () =>
      !!user.userPermissions.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user.userPermissions]
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.CUSTOMER_ORDER
  );

  const [printState, setPrintState] = useState(null);

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.CUSTOMER_ORDER.rowNumber
    );
  }, []);

  const [valuesPaginationState] = useLocalPaginationState(settings?.rowNumber);

  const [
    getDataListOfSaleOrderPendingSap,
    {
      loading,
      data
      // refetch
    }
  ] = useListOfSaleOrderPendingSapLazyQuery({
    onCompleted: () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    },
    onError: () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    },
    fetchPolicy: "no-cache"
  });

  const handleSort = async (field, id) => {
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    navigate(
      PendingOrderListUrl({
        ...params,
        ...getSortUrlVariables(field, { ...params, soldToId: id })
      }),
      { replace: true }
    );
    // await refetch({
    //   sortBy: [
    //     {
    //       soldToId: id,
    //       direction: {
    //         direction: params.asc ? OrderDirection.DESC : OrderDirection.ASC,
    //         field
    //       }
    //     }
    //   ]
    // });
    dispatchAppState({
      payload: {
        value: false
      },
      type: "displayLoader"
    });
  };

  const handleChangeFilter = (filterObj: SapListOfSaleOrderPendingInput) => {
    setPrintState(filterObj);
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    setFirstRender(false);
    getDataListOfSaleOrderPendingSap({
      variables: {
        ...valuesPaginationState,
        filter: filterObj
      }
    });
  };

  const message = useMemo(() => {
    if (firstRender) {
      return {
        icon: InfoIcon,
        message: (
          <>
            โปรดกรอกข้อมูลที่จำเป็น (
            <MyTypography variant="subtitle3" className="red">
              *
            </MyTypography>
            ) และกดค้นหา
          </>
        ),
        color: "#555555"
      };
    }
    if (!loading && !data?.listOfSaleOrderPendingSap?.length && !firstRender) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    if (!loading && data?.listOfSaleOrderPendingSap?.length) {
      return {
        icon: CheckCircle,
        message: intl.formatMessage({
          defaultMessage: "พบรายการที่เกี่ยวข้อง"
        }),
        color: "#28A745"
      };
    }
    return {
      icon: InfoIcon,
      message: (
        <>
          โปรดกรอกข้อมูลที่จำเป็น (
          <MyTypography variant="subtitle3" className="red">
            *
          </MyTypography>
          ) และกดค้นหา
        </>
      ),
      color: "#555555"
    };
  }, [data?.listOfSaleOrderPendingSap?.length, loading, firstRender]);

  const handleResetForm = () => {
    setFirstRender(true);
  };

  const downloadVariables = useMemo(
    () => ({
      input: {
        soldTo: [printState?.soldTo],
        materialNoMaterialDescription:
          printState?.materialNoMaterialDescription,
        shipTo: printState?.shipTo,
        createDate: printState?.createDate,
        requestDeliveryDate: printState?.requestDeliveryDate,
        productGroups: printState?.productGroups,
        poNo: printState?.poNo || undefined,
        soNo: printState?.soNo || undefined,
        saleOrg: printState?.saleOrg,
        transactions: printState?.transactions,
        soldToSort: [
          {
            soldTo: "",
            sortField: getSortParams(params).sort,
            sortType: getSortParams(params).asc ? "ASC" : "DESC"
          }
        ]
      }
    }),
    [printState, getSortParams(params)]
  );

  function downloadFile(file) {
    const linkSource = `data:application/pdf;base64,${file.exportedFileBase64}`;
    const downloadLink = document.createElement("a");
    const fileName = file.fileName;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
    downloadLink.remove();
  }

  const [onPrint] = usePrintPendingOrderReportMutation({
    variables: downloadVariables,
    onCompleted(data) {
      downloadFile(data.printPendingOrderReport);
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    }
  });
  const [onDownload] = useDownloadPendingOrderReportMutation({
    variables: downloadVariables,
    onCompleted(data) {
      const anchor_href =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
        data.downloadPendingOrderReport.exportedFileBase64;
      const exportLinkElement = document.createElement("a");

      exportLinkElement.hidden = true;
      exportLinkElement.download = data.downloadPendingOrderReport.fileName;
      exportLinkElement.href = anchor_href;
      document.body.appendChild(exportLinkElement);
      exportLinkElement.click();
      exportLinkElement.remove();
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    }
  });

  return (
    <div data-testid="pending-order-view-customer">
      <OrderFilterSectionCustomer
        message={<SectionMessage {...message} />}
        onResetForm={handleResetForm}
        handleChangeFilter={handleChangeFilter}
        listOrder={data?.listOfSaleOrderPendingSap}
        loading={loading}
      />

      {(!firstRender && data?.listOfSaleOrderPendingSap?.length) || loading ? (
        <OrderListTableAdmin
          isCustomer={isCustomer}
          data={data?.listOfSaleOrderPendingSap || []}
          onSort={handleSort}
          sort={getSortParams(params)}
          filterData={downloadVariables.input}
        />
      ) : (
        <></>
      )}
      {data?.listOfSaleOrderPendingSap?.length && !firstRender ? (
        <BottomBar
          dataCount={1}
          onDownload={() => {
            onDownload();
            dispatchAppState({
              payload: {
                value: true
              },
              type: "displayLoader"
            });
          }}
          onPrint={() => {
            onPrint();
            dispatchAppState({
              payload: {
                value: true
              },
              type: "displayLoader"
            });
          }}
        />
      ) : null}
    </div>
  );
};

export default PendingOrderViewCustomer;
